import { html, render } from 'lit-html';
import emailjs from '@emailjs/browser';
import * as brandStyles from 'url:../../brand.css';
import * as globalStyles from 'url:../../global.css';
import * as formStyles from 'url:./style.css';

class SignUp extends HTMLElement {
  static fallbackEmail = 'danieljsitar@gmail.com';
  static fallbackPhoneDisplay = '(978) 302-7411';
  static fallbackPhoneLink = '+19783027411';
  static defaultEmailJsConfig = Object.freeze({
    serviceId: 'service_7016i18',
    templateId: 'template_3r56m5k',
    publicKey: 'upWPh-GuDqJV6xAEY',
  });

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: 'open' });

    this.onDocClick = this.onDocClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onToggle = this.onToggle.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onPopupClose = this.onPopupClose.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }

  connectedCallback() {
    this.render();
    this.cacheEls();
    this.attachEvents();
    this.initLogoNotes();
  }

  disconnectedCallback() {
    this.detachEvents();
    if (this._notesTimer) clearInterval(this._notesTimer);
    this.clearPopupTimer();
    this.clearCloseTimer();
  }

  render() {
    const template = html`
      <link href="${brandStyles}" rel="stylesheet" />
      <link href="${globalStyles}" rel="stylesheet" />
      <link href="${formStyles}" rel="stylesheet" />

      <button
        id="contact-slide-toggle"
        aria-haspopup="dialog"
        aria-expanded="false"
        class="frijole-regular"
      >
        ${this.getAttribute('button-label') || 'Sign Up'}
        <!-- Notes animation -->
        <span class="logo-notes"></span>
      </button>

      <aside
        id="contact-slide-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="contact-form-title"
      >
        <button
          id="contact-slide-close"
          aria-label="Close sign-up form"
          type="button"
        >
          &times;
        </button>

        <form id="contact-form" class="form-card" novalidate>
          <h2 id="contact-form-title" class="frijole-regular">Quick Sign-Up</h2>
          <div class="quick-contact-links">
            <p class="quick-contact-item">
              Prefer email?
              <a href="mailto:${SignUp.fallbackEmail}">${SignUp.fallbackEmail}</a>
            </p>
            <p class="quick-contact-item">
              Prefer phone?
              <a href="tel:${SignUp.fallbackPhoneLink}">${SignUp.fallbackPhoneDisplay}</a>
            </p>
          </div>

          <div class="form-grid">
            <div class="field">
              <label for="name">Name:</label>
              <input type="text" id="name" name="name" autocomplete="name" />
            </div>

            <div class="field">
              <label>What would you like to study:</label>
              <div class="study-bubbles" data-hidden="#study" data-multi="true">
                <button type="button" class="study-bubble" data-value="guitar">
                  Guitar
                </button>
                <button type="button" class="study-bubble" data-value="piano">
                  Piano
                </button>
                <button type="button" class="study-bubble" data-value="drums">
                  Drums
                </button>
                <button type="button" class="study-bubble" data-value="bass">
                  Bass
                </button>
                <button
                  type="button"
                  class="study-bubble"
                  data-value="recording"
                >
                  Recording
                </button>
                <button
                  type="button"
                  class="study-bubble"
                  data-value="production"
                >
                  Production
                </button>
                <button type="button" class="study-bubble" data-value="mixing">
                  Mixing
                </button>
                <button
                  type="button"
                  class="study-bubble"
                  data-value="mastering"
                >
                  Mastering
                </button>
                <button
                  type="button"
                  class="study-bubble"
                  data-value="music theory"
                >
                  Music Theory
                </button>
                <button
                  type="button"
                  class="study-bubble"
                  data-value="songwriting"
                >
                  Songwriting
                </button>
              </div>
              <input type="hidden" id="study" name="study" value="" />
            </div>

            <div class="field">
              <label for="experience">How much experience do you have:</label>
              <input
                type="text"
                id="experience"
                name="experience"
                autocomplete="off"
              />
            </div>

            <div class="field">
              <label for="email"
                >E-mail address: <span class="required">*</span></label
              >
              <input
                type="email"
                id="email"
                name="email"
                required
                autocomplete="email"
              />
            </div>

            <div class="field">
              <label for="genres">Genres you’re into:</label>
              <input type="text" id="genres" name="genres" autocomplete="off" />
            </div>

            <div class="field">
              <label for="goals">Your goals:</label>
              <textarea id="goals" name="goals"></textarea>
            </div>

            <div class="field">
              <label>Days available:</label>
              <div
                class="days-bubbles"
                data-hidden="#day"
                data-multi="true"
                data-sync-pill="day"
              >
                <button type="button" class="days-bubble" data-value="Monday">
                  Monday
                </button>
                <button type="button" class="days-bubble" data-value="Tuesday">
                  Tuesday
                </button>
                <button
                  type="button"
                  class="days-bubble"
                  data-value="Wednesday"
                >
                  Wednesday
                </button>
                <button type="button" class="days-bubble" data-value="Thursday">
                  Thursday
                </button>
              </div>
              <input type="hidden" id="day" name="day" value="" />
            </div>

            <div class="field">
              <label for="time">Times available (1 PM ET – 9 PM ET):</label>
              <input type="text" id="time" name="time" autocomplete="off" />
            </div>

            <div class="field">
              <label>Lesson length:</label>
              <div class="length-bubbles" data-hidden="#length">
                <button type="button" class="length-bubble" data-value="30 min">
                  30 mins
                </button>
                <button type="button" class="length-bubble" data-value="45 min">
                  45 mins
                </button>
                <button type="button" class="length-bubble" data-value="60 min">
                  60 mins
                </button>
              </div>
              <input type="hidden" id="length" name="length" value="" />
            </div>

            <div class="field">
              <label>Lesson recurrence:</label>
              <div class="recurrence-bubbles" data-hidden="#recurrence">
                <button
                  type="button"
                  class="recurrence-bubble"
                  data-value="Single"
                >
                  Single
                </button>
                <button
                  type="button"
                  class="recurrence-bubble"
                  data-value="Weekly"
                >
                  Weekly
                </button>
              </div>
              <input type="hidden" id="recurrence" name="recurrence" value="" />
            </div>

            <div class="field">
              <label for="referral">How’d you hear about me?</label>
              <input
                type="text"
                id="referral"
                name="referral"
                autocomplete="off"
              />
            </div>

            <div class="field checkbox-field full-span">
              <input
                type="checkbox"
                id="age_confirm"
                name="age_confirm"
                required
              />
              <label for="age_confirm"
                >Student is over 6 years of age
                <span class="required">*</span></label
              >
            </div>

            <div class="field full-span">
              <label for="additional">Anything else:</label>
              <textarea id="additional" name="additional"></textarea>
            </div>
          </div>

          <button type="submit" class="btn full-span">Submit</button>
        </form>
      </aside>
      <div id="contact-popup" class="contact-popup" hidden>
        <div
          id="contact-popup-card"
          class="contact-popup-card"
          role="status"
          aria-live="polite"
        >
          <span id="contact-popup-icon" class="popup-icon" aria-hidden="true"
            >!</span
          >
          <div class="popup-copy">
            <p id="contact-popup-title" class="popup-title"></p>
            <p id="contact-popup-text" class="popup-text"></p>
          </div>
          <button
            id="contact-popup-close"
            type="button"
            class="popup-close"
            aria-label="Dismiss message"
          >
            x
          </button>
        </div>
      </div>
    `;
    render(template, this.shadow);
  }

  cacheEls() {
    this.$toggle = this.shadow.getElementById('contact-slide-toggle');
    this.$panel = this.shadow.getElementById('contact-slide-panel');
    this.$close = this.shadow.getElementById('contact-slide-close');
    this.$form = this.shadow.getElementById('contact-form');
    this.$notesHost = this.$toggle.querySelector('.logo-notes');
    this.$popup = this.shadow.getElementById('contact-popup');
    this.$popupCard = this.shadow.getElementById('contact-popup-card');
    this.$popupIcon = this.shadow.getElementById('contact-popup-icon');
    this.$popupTitle = this.shadow.getElementById('contact-popup-title');
    this.$popupText = this.shadow.getElementById('contact-popup-text');
    this.$popupClose = this.shadow.getElementById('contact-popup-close');
  }

  attachEvents() {
    this.$toggle.addEventListener('click', this.onToggle);
    this.$close.addEventListener('click', this.onClose);

    this.shadow
      .querySelectorAll(
        '.study-bubbles, .length-bubbles, .recurrence-bubbles, .days-bubbles',
      )
      .forEach((group) =>
        group.addEventListener('click', (e) => this.onBubbleClick(e, group)),
      );

    this.$form.addEventListener('submit', this.onSubmit);
    this.$popupClose?.addEventListener('click', this.onPopupClose);

    document.addEventListener('click', this.onDocClick, true);
    document.addEventListener('keydown', this.onKeyDown, true);
  }

  detachEvents() {
    this.$toggle?.removeEventListener('click', this.onToggle);
    this.$close?.removeEventListener('click', this.onClose);
    this.$form?.removeEventListener('submit', this.onSubmit);
    this.$popupClose?.removeEventListener('click', this.onPopupClose);
    document.removeEventListener('click', this.onDocClick, true);
    document.removeEventListener('keydown', this.onKeyDown, true);
  }

  // Panel
  open() {
    this.$panel.classList.add('open');
    this.$toggle.setAttribute('aria-expanded', 'true');
    queueMicrotask(() => this.shadow.getElementById('name')?.focus());
  }
  close() {
    this.$panel.classList.remove('open');
    this.$toggle.setAttribute('aria-expanded', 'false');
    this.hidePopup({ immediate: true });
  }
  onToggle() {
    this.$panel.classList.contains('open') ? this.close() : this.open();
  }
  onClose() {
    this.close();
  }
  onPopupClose() {
    this.hidePopup();
  }
  onDocClick(e) {
    const path = e.composedPath?.() || [];
    if (!path.includes(this.$panel) && !path.includes(this.$toggle))
      this.close();
  }
  onKeyDown(e) {
    if (e.key === 'Escape' && this.$panel.classList.contains('open'))
      this.close();
  }

  // Bubbles (supports single + multi, and optional day-pill mirroring)
  onBubbleClick(e, group) {
    const btn = e.target.closest('button[data-value]');
    if (!btn) return;

    const multi = group.dataset.multi === 'true';
    const hidden = group.dataset.hidden
      ? this.shadow.querySelector(group.dataset.hidden)
      : null;
    if (!hidden) return;

    if (multi) {
      btn.classList.toggle('selected');
      const values = [...group.querySelectorAll('button.selected')].map(
        (b) => b.dataset.value,
      );
      hidden.value = values.join(', ');
    } else {
      group
        .querySelectorAll('button.selected')
        .forEach((b) => b.classList.remove('selected'));
      if (!btn.classList.contains('selected')) {
        btn.classList.add('selected');
        hidden.value = btn.dataset.value;
      } else {
        hidden.value = '';
      }
    }

    if (group.dataset.syncPill === 'day') {
      const val = btn.dataset.value;
      const pills = document.querySelectorAll(`.day-pill[data-day="${val}"]`);
      pills.forEach((p) =>
        p.classList.toggle('active', btn.classList.contains('selected')),
      );
    }
  }

  getEmailJsConfig() {
    const getAttr = (name) => {
      const value = this.getAttribute(name);
      if (typeof value !== 'string') return '';
      return value.trim();
    };

    const globalConfig = window.__EMAILJS_CONFIG__ || {};
    const config = {
      serviceId:
        getAttr('emailjs-service-id') ||
        globalConfig.serviceId ||
        SignUp.defaultEmailJsConfig.serviceId,
      templateId:
        getAttr('emailjs-template-id') ||
        globalConfig.templateId ||
        SignUp.defaultEmailJsConfig.templateId,
      publicKey:
        getAttr('emailjs-public-key') ||
        globalConfig.publicKey ||
        SignUp.defaultEmailJsConfig.publicKey,
    };

    const missing = Object.entries(config)
      .filter(([, value]) => !value)
      .map(([key]) => key);

    if (missing.length) {
      console.warn(`Missing EmailJS configuration keys: ${missing.join(', ')}`);
      return null;
    }

    return config;
  }

  // Submit via EmailJS
  async onSubmit(e) {
    e.preventDefault();
    this.hidePopup({ immediate: true });

    const email = this.shadow.getElementById('email');
    const age = this.shadow.getElementById('age_confirm');
    if (!email.checkValidity() || !age.checked) {
      email.reportValidity?.();
      if (!age.checked) this.shadow.getElementById('age_confirm')?.focus();
      return;
    }

    const submitBtn = this.$form.querySelector('.btn');
    submitBtn.disabled = true;

    const payload = Object.fromEntries(new FormData(this.$form).entries());

    try {
      const emailJsConfig = this.getEmailJsConfig();
      if (!emailJsConfig) {
        throw new Error('EmailJS configuration is missing.');
      }

      await emailjs.sendForm(
        emailJsConfig.serviceId,
        emailJsConfig.templateId,
        this.$form,
        { publicKey: emailJsConfig.publicKey },
      );

      this.showEmojiConfetti(submitBtn);
      this.showSubmitPopup({
        title: 'Thanks, your message was sent',
        text: 'We received your sign-up and will follow up soon.',
        type: 'success',
      });
      this.resetForm();
      this.clearCloseTimer();
      this._closeTimer = setTimeout(() => this.close(), 1800);
    } catch (err) {
      console.error('Submit error:', err);
      this.showSubmitPopup({
        title: 'Message ready to send',
        text: 'We opened your email app with your details prefilled. Please review and tap send.',
        type: 'fallback',
      });
      this.openFallbackEmail(payload);
    } finally {
      submitBtn.disabled = false;
    }
  }

  buildFallbackEmailBody(payload) {
    const getValue = (key) => {
      if (key === 'age_confirm') return payload[key] === 'on' ? 'Yes' : 'No';
      const value = payload[key];
      if (typeof value !== 'string') return '—';
      const trimmed = value.trim();
      return trimmed || '—';
    };

    const fields = [
      ['Name', 'name'],
      ['Email', 'email'],
      ['Wants to study', 'study'],
      ['Experience', 'experience'],
      ['Genres', 'genres'],
      ['Goals', 'goals'],
      ['Days available', 'day'],
      ['Times available', 'time'],
      ['Lesson length', 'length'],
      ['Recurrence', 'recurrence'],
      ['Referral', 'referral'],
      ['Student over 6', 'age_confirm'],
      ['Additional notes', 'additional'],
    ];

    return [
      'Website form delivery failed, so I am sending this directly from my email app.',
      '',
      ...fields.map(([label, key]) => `${label}: ${getValue(key)} /`),
    ].join('\n');
  }

  openFallbackEmail(payload) {
    const name = (payload.name || '').trim();
    const subject = name
      ? `Lesson Sign-Up (Fallback): ${name}`
      : 'Lesson Sign-Up (Fallback)';
    const body = this.buildFallbackEmailBody(payload);
    const mailto = `mailto:${SignUp.fallbackEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
  }

  clearPopupTimer() {
    if (this._popupTimer) {
      clearTimeout(this._popupTimer);
      this._popupTimer = null;
    }
  }

  clearCloseTimer() {
    if (this._closeTimer) {
      clearTimeout(this._closeTimer);
      this._closeTimer = null;
    }
  }

  showSubmitPopup({ title, text, type = 'fallback' }) {
    if (!this.$popup || !this.$popupCard || !this.$popupTitle || !this.$popupText)
      return;
    this.clearPopupTimer();
    this.$popupCard.classList.remove('popup-fallback', 'popup-success');
    this.$popupCard.classList.add(`popup-${type}`);
    if (this.$popupIcon) this.$popupIcon.textContent = type === 'success' ? '✓' : '!';
    this.$popupTitle.textContent = title;
    this.$popupText.textContent = text;
    this.$popup.hidden = false;
    requestAnimationFrame(() => this.$popup.classList.add('show'));
    this._popupTimer = setTimeout(() => this.hidePopup(), 7000);
  }

  hidePopup({ immediate = false } = {}) {
    if (!this.$popup || !this.$popupCard) return;
    this.clearPopupTimer();
    if (immediate || !this.$popup.classList.contains('show')) {
      this.$popup.classList.remove('show');
      this.$popupCard.classList.remove('popup-fallback', 'popup-success');
      this.$popup.hidden = true;
      return;
    }
    this.$popup.classList.remove('show');
    setTimeout(() => {
      if (!this.$popup.classList.contains('show')) this.$popup.hidden = true;
    }, 240);
  }

  resetForm() {
    this.$form.reset();
    this.shadow
      .querySelectorAll('.selected')
      .forEach((el) => el.classList.remove('selected'));
    // Clear hidden inputs
    ['study', 'day', 'length', 'recurrence'].forEach((id) => {
      const el = this.shadow.getElementById(id);
      if (el) el.value = '';
    });
    document
      .querySelectorAll('.day-pill.active')
      .forEach((p) => p.classList.remove('active'));
  }

  // Floating Notes
  initLogoNotes() {
    const notesPool = [
      '\u266B',
      '\u266A',
      '\u266C',
      '\u2669',
      '\u266F',
      '\u266D',
    ];
    const host = this.$notesHost;
    if (!host) return;

    const createNotes = () => {
      // Create a short burst of notes
      for (let i = 0; i < 6; i++) {
        const note = document.createElement('span');
        note.className = 'logo-note';
        note.textContent =
          notesPool[Math.floor(Math.random() * notesPool.length)];
        note.style.left = `${40 + i * 20 - Math.random() * 20}px`;
        note.style.animationDelay = `${i * 0.12 + Math.random() * 0.08}s`;
        host.appendChild(note);
        note.addEventListener('animationend', () => note.remove());
      }
    };

    createNotes();
    this._notesTimer = setInterval(createNotes, 5000);
  }

  // Emoji confetti
  showEmojiConfetti(targetBtn) {
    const emojis = ['🎵', '🎶', '🎼', '🎤', '🎹', '🥁', '🎷', '🎺', '🪗', '🪕'];
    const rect = targetBtn.getBoundingClientRect();
    for (let i = 0; i < 28; i++) {
      const emoji = document.createElement('span');
      emoji.textContent = emojis[Math.floor(Math.random() * emojis.length)];
      Object.assign(emoji.style, {
        position: 'fixed',
        left: rect.left + rect.width / 2 + 'px',
        top: rect.top + rect.height / 2 + 'px',
        fontSize: `${2 + Math.random() * 2.5}rem`,
        fontFamily: `'Apple Color Emoji','Segoe UI Emoji','Segoe UI Symbol','Noto Color Emoji',sans-serif`,
        pointerEvents: 'none',
        zIndex: 9999,
        transition:
          'transform 2.4s cubic-bezier(0.18, 0.7, 0.32, 1), opacity 2.4s',
        textShadow: '0 2px 8px #fff, 0 1px 0 #fff',
      });
      document.body.appendChild(emoji);

      const angle = -Math.PI / 2 + (Math.random() - 0.5) * (Math.PI / 2);
      const distance = 700 + Math.random() * 400;
      const x = Math.cos(angle) * distance;
      const y = Math.sin(angle) * distance;

      setTimeout(() => {
        emoji.style.transform = `translate(${x}px, ${y}px) scale(${0.8 + Math.random() * 1.2}) rotate(${Math.random() * 2160}deg)`;
        emoji.style.opacity = 0;
      }, 10);

      setTimeout(() => emoji.remove(), 2500);
    }
  }
}

customElements.define('sign-up', SignUp);
